import type {
  PromoCodeState,
  SavedCartPayloadV1,
  SerializableCartItem,
} from "./cart-persistence";
import { emptySavedCartPayload } from "./cart-persistence";

export type CartMergeSummary = {
  /** Distinct lines that existed only on server (before merge). */
  accountOnlyLines: number;
  /** Distinct lines that existed only on device (before merge). */
  deviceOnlyLines: number;
  /** Lines that appeared in both (same productId+packageId in same store bucket or temp). */
  overlappingLineKeys: number;
  /** Whether promo was taken from device vs account for the active store bucket. */
  promoSource: "device" | "account" | "none";
};

function lineKey(i: SerializableCartItem): string {
  return `${i.productId}::${i.packageId ?? ""}`;
}

function mergeLineItems(
  a: SerializableCartItem[],
  b: SerializableCartItem[]
): SerializableCartItem[] {
  const map = new Map<string, SerializableCartItem>();

  const ingest = (items: SerializableCartItem[]) => {
    for (const it of items) {
      if (!it?.productId || !Number.isFinite(Number(it.quantity)) || it.quantity < 1) {
        continue;
      }
      const k = lineKey(it);
      const ex = map.get(k);
      if (ex) {
        map.set(k, {
          ...ex,
          quantity: ex.quantity + it.quantity,
          price: Number.isFinite(it.price) ? it.price : ex.price,
          name: it.name || ex.name,
          catalogItemId: it.catalogItemId || ex.catalogItemId,
          actualPackageId:
            it.actualPackageId !== undefined
              ? it.actualPackageId
              : ex.actualPackageId,
        });
      } else {
        map.set(k, { ...it, quantity: Math.max(1, Math.floor(it.quantity)) });
      }
    }
  };

  ingest(a);
  ingest(b);
  return Array.from(map.values());
}

function countLineKeys(items: SerializableCartItem[]): Set<string> {
  return new Set(items.map(lineKey));
}

function mergePromo(
  device: PromoCodeState | null,
  account: PromoCodeState | null
): { merged: PromoCodeState | null; source: "device" | "account" | "none" } {
  const deviceHas = !!(device?.code || device?.promoCodeId);
  const accountHas = !!(account?.code || account?.promoCodeId);
  if (deviceHas && accountHas) {
    return { merged: device, source: "device" };
  }
  if (deviceHas) return { merged: device, source: "device" };
  if (accountHas) return { merged: account, source: "account" };
  return { merged: null, source: "none" };
}

/**
 * Deterministic merge: union of store buckets; quantities sum for matching lines.
 * Active store & fulfillment prefer device (guest session) when set.
 */
export function mergeSavedCartPayloads(
  device: SavedCartPayloadV1,
  account: SavedCartPayloadV1 | null
): { merged: SavedCartPayloadV1; summary: CartMergeSummary } {
  if (!account || account.v !== 1) {
    return {
      merged: { ...device, v: 1 },
      summary: {
        accountOnlyLines: 0,
        deviceOnlyLines: countLineKeys(
          Object.values(device.perStore).flatMap((b) => b.items)
        ).size,
        overlappingLineKeys: 0,
        promoSource: "none",
      },
    };
  }

  const storeIds = new Set([
    ...Object.keys(device.perStore),
    ...Object.keys(account.perStore),
  ]);

  const perStore: SavedCartPayloadV1["perStore"] = {};
  let accountOnlyLines = 0;
  let deviceOnlyLines = 0;
  let overlappingLineKeys = 0;

  for (const sid of storeIds) {
    const d = device.perStore[sid]?.items ?? [];
    const a = account.perStore[sid]?.items ?? [];
    const dk = countLineKeys(d);
    const ak = countLineKeys(a);
    for (const k of dk) {
      if (!ak.has(k)) deviceOnlyLines++;
    }
    for (const k of ak) {
      if (!dk.has(k)) accountOnlyLines++;
    }
    for (const k of dk) {
      if (ak.has(k)) overlappingLineKeys++;
    }

    const dPromo = device.perStore[sid]?.promoCode ?? null;
    const aPromo = account.perStore[sid]?.promoCode ?? null;
    const { merged: pMerged } = mergePromo(dPromo, aPromo);

    perStore[sid] = {
      items: mergeLineItems(a, d),
      promoCode: pMerged,
    };
  }

  const tempItems = mergeLineItems(device.temp.items, account.temp.items);
  const tempPromo = mergePromo(device.temp.promoCode, account.temp.promoCode);

  const activeStore =
    device.storeCovaLocationId ?? account.storeCovaLocationId ?? null;
  const fulfillmentMode =
    device.storeCovaLocationId != null
      ? device.fulfillmentMode
      : account.fulfillmentMode;

  const activeSid = activeStore != null ? String(activeStore) : null;
  let promoSource: CartMergeSummary["promoSource"] = "none";
  if (activeSid && perStore[activeSid]) {
    const dP = device.perStore[activeSid]?.promoCode ?? null;
    const aP = account.perStore[activeSid]?.promoCode ?? null;
    promoSource = mergePromo(dP, aP).source;
  } else {
    promoSource = tempPromo.source;
  }

  return {
    merged: {
      v: 1,
      storeCovaLocationId: activeStore,
      fulfillmentMode,
      perStore,
      temp: { items: tempItems, promoCode: tempPromo.merged },
    },
    summary: {
      accountOnlyLines,
      deviceOnlyLines,
      overlappingLineKeys,
      promoSource,
    },
  };
}

export function normalizeRemoteCartPayload(raw: unknown): SavedCartPayloadV1 {
  if (!raw || typeof raw !== "object") {
    return emptySavedCartPayload();
  }
  const r = raw as Record<string, unknown>;
  if (r.v === 1 && r.perStore && typeof r.perStore === "object" && r.temp && typeof r.temp === "object") {
    return r as unknown as SavedCartPayloadV1;
  }

  const storeCovaLocationId =
    typeof r.storeCovaLocationId === "number" && r.storeCovaLocationId > 0
      ? r.storeCovaLocationId
      : null;
  const fulfillmentMode =
    r.fulfillmentMode === "delivery" ? "delivery" : "pickup";
  const items = Array.isArray(r.items) ? (r.items as SerializableCartItem[]) : [];
  const promoCode = (r.promoCode ?? null) as PromoCodeState | null;
  const perStore: SavedCartPayloadV1["perStore"] = {};
  if (storeCovaLocationId) {
    perStore[String(storeCovaLocationId)] = { items, promoCode };
    return {
      v: 1,
      storeCovaLocationId,
      fulfillmentMode,
      perStore,
      temp: { items: [], promoCode: null },
    };
  }
  if (items.length > 0) {
    return {
      v: 1,
      storeCovaLocationId: null,
      fulfillmentMode,
      perStore: {},
      temp: { items, promoCode },
    };
  }
  return {
    v: 1,
    storeCovaLocationId: null,
    fulfillmentMode,
    perStore: {},
    temp: { items: [], promoCode: null },
  };
}

export function formatCartMergeNotice(summary: CartMergeSummary): string {
  const parts: string[] = [
    "Signed in: we merged your cart from this device with your saved cart.",
  ];
  if (summary.overlappingLineKeys > 0) {
    parts.push(
      `For matching products, quantities were added together (${summary.overlappingLineKeys} overlap${summary.overlappingLineKeys === 1 ? "" : "s"}).`
    );
  }
  if (summary.accountOnlyLines > 0) {
    parts.push(
      `Restored ${summary.accountOnlyLines} line${summary.accountOnlyLines === 1 ? "" : "s"} from your account.`
    );
  }
  if (summary.deviceOnlyLines > 0) {
    parts.push(
      `Kept ${summary.deviceOnlyLines} line${summary.deviceOnlyLines === 1 ? "" : "s"} from this device.`
    );
  }
  if (summary.promoSource === "device") {
    parts.push("Promo code on this device was kept where it conflicted with your account.");
  } else if (summary.promoSource === "account") {
    parts.push("Promo code from your account was applied.");
  }
  return parts.join(" ");
}
